import { useMutation, useQuery } from "@tanstack/react-query";
import type { Package as T_RevenueCatPackage } from "@revenuecat/purchases-js";
import React, { useState } from "react";

import {
  BILLING_PURCHASE_INTENTS_BY_PLAN_PERIOD,
  E_BillingCatalogSurface,
  E_BillingClientPlatform,
  E_BillingPeriod,
  E_BillingPlanKey,
  type T_BillingCatalog,
  type T_BillingPaidPlanKey,
} from "@lattice/core-lib/types/billing/billing-catalog.types";

import { Button } from "@lattice/frontend/components/kit/Button";
import { Pill } from "@lattice/frontend/components/kit/Pill";
import { Surface } from "@lattice/frontend/components/kit/Surface";
import {
  configureRevenueCat,
  ErrorCode,
  PurchasesError,
} from "@lattice/frontend/lib/revenuecat";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";

import {
  SettingsPanelError,
  SettingsPanelLoading,
  SettingsPanelSuccess,
  SettingsSection,
} from "./SettingsPanelState";

type T_SettingsTier = "FREE" | "PRO" | "MAX";
type T_SettingsPeriod = "ANNUAL" | "MONTHLY";
type T_WebPackageMap = Partial<
  Record<T_BillingPaidPlanKey, T_RevenueCatPackage>
>;

const PAID_TIERS = [E_BillingPlanKey.PRO, E_BillingPlanKey.MAX] as const;

export function getSettingsTier(user: {
  Subscription: { tier: string } | null;
  is_premium: boolean;
}): T_SettingsTier {
  if (!user.is_premium) return "FREE";
  if (user.Subscription?.tier === "MAX") return "MAX";
  if (user.Subscription?.tier === "PRO") return "PRO";
  return "FREE";
}

function getPurchaseOption(
  catalog: T_BillingCatalog,
  tier: T_BillingPaidPlanKey,
  period: T_SettingsPeriod,
) {
  const intent =
    BILLING_PURCHASE_INTENTS_BY_PLAN_PERIOD[tier][
      period === "MONTHLY" ? E_BillingPeriod.MONTHLY : E_BillingPeriod.ANNUAL
    ];
  return (
    catalog.purchase_options.find(
      (option) => option.purchase_intent === intent,
    ) ?? null
  );
}

function getPackagePrice(
  pkg: T_RevenueCatPackage | undefined,
  period: T_SettingsPeriod,
): string {
  if (!pkg) return "Unavailable";
  const price = pkg.webBillingProduct.price;
  if (period === "MONTHLY") return `${price.formattedPrice}/month`;
  return `${price.formattedPrice}/year`;
}

async function loadRevenueCatPackages(args: {
  appEnvironment: string;
  catalog: T_BillingCatalog;
  period: T_SettingsPeriod;
  revenueCatApiKey: string;
  userCuid: string;
}): Promise<T_WebPackageMap> {
  const purchases = configureRevenueCat(
    args.revenueCatApiKey,
    `${args.userCuid}_${args.appEnvironment}`,
  );
  const offerings = await purchases.getOfferings();
  const offering =
    offerings.all[args.catalog.offering.revenuecat_offering_id] ??
    (args.catalog.offering.key === "standard" ? offerings.current : null);
  const packages: T_WebPackageMap = {};
  for (const tier of PAID_TIERS) {
    const option = getPurchaseOption(args.catalog, tier, args.period);
    const pkg = option
      ? offering?.packagesById[option.revenuecat_package_id]
      : undefined;
    if (pkg) packages[tier] = pkg;
  }
  return packages;
}

function isCancelledPurchase(error: Error): boolean {
  return (
    error instanceof PurchasesError &&
    error.errorCode === ErrorCode.UserCancelledError
  );
}

function getPurchaseErrorMessage(error: Error): string | null {
  if (isCancelledPurchase(error)) return null;
  return error.message || "Purchase could not be completed.";
}

export type T_SubscriptionPanelViewProps = {
  cancelling: boolean;
  cancellationError: string | null;
  currentTier: T_SettingsTier;
  planPrices: Record<T_BillingPaidPlanKey, string | null>;
  period: T_SettingsPeriod;
  productsLoading: boolean;
  purchaseError: string | null;
  purchased: boolean;
  purchasingTier: T_BillingPaidPlanKey | null;
  subscription: {
    endDate: Date | null;
    status: string | null;
    type: string | null;
  };
  onCancelSubscription: () => void;
  onManageStore: (type: "APPLE" | "GOOGLE") => void;
  onPeriodChange: (period: T_SettingsPeriod) => void;
  onPurchase: (tier: T_BillingPaidPlanKey) => void;
};

export function SubscriptionPanelView({
  cancelling,
  cancellationError,
  currentTier,
  planPrices,
  period,
  productsLoading,
  purchaseError,
  purchased,
  purchasingTier,
  subscription,
  onCancelSubscription,
  onManageStore,
  onPeriodChange,
  onPurchase,
}: T_SubscriptionPanelViewProps) {
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const isPaid = currentTier !== "FREE";
  const isCancelled = subscription.status === "CANCELLED";
  const storeType =
    subscription.type === "APPLE" || subscription.type === "GOOGLE"
      ? subscription.type
      : null;

  return (
    <div className="shell-settings-form">
      <Surface tone={isPaid ? "accent" : "subtle"}>
        <span className="shell-settings-summary__plan">
          {isCancelled ? "Cancellation scheduled" : isPaid ? "Active" : "Free"}
        </span>
        <p className="shell-settings-subscription__tier">
          Lattice{" "}
          {currentTier === "FREE"
            ? "Free"
            : currentTier === "PRO"
              ? "Pro"
              : "Max"}
        </p>
        <p className="shell-settings__body">
          {isCancelled
            ? `Access continues${subscription.endDate ? ` until ${subscription.endDate.toLocaleDateString()}` : " through the current billing period"}.`
            : isPaid
              ? "Your paid access is active."
              : "Upgrade for more Prism research, event alerts, and portfolio insight."}
        </p>
      </Surface>

      <SettingsSection label="Billing period">
        <div className="shell-settings-pill-grid">
          <Pill
            label="Annual"
            onClick={() => onPeriodChange("ANNUAL")}
            selected={period === "ANNUAL"}
          />
          <Pill
            label="Monthly"
            onClick={() => onPeriodChange("MONTHLY")}
            selected={period === "MONTHLY"}
          />
        </div>
      </SettingsSection>

      <div className="shell-settings-card-stack">
        {PAID_TIERS.map((tier) => {
          const current = currentTier === tier;
          return (
            <Surface key={tier} tone={current ? "accent" : "subtle"}>
              <div className="shell-settings-subscription__plan-row">
                <span>
                  <strong>Lattice {tier === "PRO" ? "Pro" : "Max"}</strong>
                  <small>
                    {productsLoading
                      ? "Loading price…"
                      : (planPrices[tier] ?? "Unavailable")}
                  </small>
                </span>
                <Button
                  disabled={current || planPrices[tier] === null}
                  label={
                    current
                      ? "Current"
                      : currentTier === "FREE"
                        ? "Upgrade"
                        : "Change"
                  }
                  loading={purchasingTier === tier}
                  onClick={() => onPurchase(tier)}
                  size="compact"
                />
              </div>
            </Surface>
          );
        })}
      </div>

      {storeType ? (
        <Button
          label={`Manage in ${storeType === "APPLE" ? "App Store" : "Google Play"}`}
          onClick={() => onManageStore(storeType)}
          variant="secondary"
        />
      ) : isPaid && !isCancelled ? (
        cancelConfirmationOpen ? (
          <Surface tone="subtle">
            <p className="shell-settings-delete__title">Cancel subscription?</p>
            <p className="shell-settings__body">
              Paid access continues until the end of the current billing period.
            </p>
            <div className="shell-settings-actions">
              <Button
                label="Keep plan"
                onClick={() => setCancelConfirmationOpen(false)}
                variant="secondary"
              />
              <Button
                label="Confirm cancellation"
                loading={cancelling}
                onClick={onCancelSubscription}
                variant="danger"
              />
            </div>
          </Surface>
        ) : (
          <Button
            label="Cancel subscription"
            onClick={() => setCancelConfirmationOpen(true)}
            variant="danger"
          />
        )
      ) : null}

      {purchaseError ? <SettingsPanelError message={purchaseError} /> : null}
      {cancellationError ? (
        <SettingsPanelError message={cancellationError} />
      ) : null}
      {purchased ? (
        <SettingsPanelSuccess message="Your subscription is active." />
      ) : null}
    </div>
  );
}

export function SubscriptionPanel({
  appEnvironment,
  paymentEnabled,
  revenueCatApiKey,
}: {
  appEnvironment: string;
  paymentEnabled: boolean;
  revenueCatApiKey: string;
}) {
  const [period, setPeriod] = useState<T_SettingsPeriod>("ANNUAL");
  const utils = trpcApi.useUtils();
  const currentUser = trpcApi.users.get_current_user.useQuery({});
  const catalogQuery = trpcApi.payment.get_billing_catalog.useQuery(
    {
      platform: E_BillingClientPlatform.WEB,
      surface: E_BillingCatalogSurface.WEB_PRICING,
    },
    { enabled: paymentEnabled },
  );
  const catalog = catalogQuery.data?.data ?? null;
  const products = useQuery({
    enabled:
      paymentEnabled && currentUser.data !== undefined && catalog !== null,
    queryKey: [
      "settings-revenuecat-products",
      currentUser.data,
      currentUser.data?.cuid ?? null,
      catalog,
      period,
      revenueCatApiKey,
      appEnvironment,
    ],
    queryFn: async (): Promise<T_WebPackageMap> => {
      if (!currentUser.data || !catalog) return {};
      return loadRevenueCatPackages({
        appEnvironment,
        catalog,
        period,
        revenueCatApiKey,
        userCuid: currentUser.data.cuid,
      });
    },
    staleTime: 5 * 60 * 1000,
  });
  const purchase = useMutation({
    mutationFn: async (tier: T_BillingPaidPlanKey) => {
      if (!currentUser.data)
        throw new Error("Account details are still loading.");
      const pkg = products.data?.[tier];
      if (!pkg) throw new Error("Selected plan is not available.");
      trackEvent(MixpanelEvents.PURCHASE_INITIATED, { period, plan: tier });
      const purchases = configureRevenueCat(
        revenueCatApiKey,
        `${currentUser.data.cuid}_${appEnvironment}`,
      );
      const result = await purchases.purchase({ rcPackage: pkg });
      if (Object.keys(result.customerInfo.entitlements.active).length === 0) {
        throw new Error(
          "Purchase completed but account activation is pending.",
        );
      }
      trackEvent(MixpanelEvents.PURCHASE_SUCCEEDED, { period, plan: tier });
      await utils.users.get_current_user.invalidate();
    },
    onError: (error) => {
      if (isCancelledPurchase(error)) return;
      trackEvent(MixpanelEvents.PURCHASE_FAILED, { error: error.message });
    },
  });
  const cancelSubscription = trpcApi.payment.cancel_subscription.useMutation({
    onSuccess: async () => {
      await utils.users.get_current_user.invalidate();
    },
  });

  if (!paymentEnabled) {
    return (
      <SettingsPanelError message="Billing controls are currently unavailable." />
    );
  }
  if (currentUser.isPending || catalogQuery.isPending) {
    return <SettingsPanelLoading label="Loading subscription" />;
  }
  if (
    currentUser.isError ||
    catalogQuery.isError ||
    !currentUser.data ||
    !catalog
  ) {
    return (
      <SettingsPanelError message="Subscription details could not be loaded." />
    );
  }

  const subscription = currentUser.data.Subscription;
  return (
    <SubscriptionPanelView
      cancelling={cancelSubscription.isPending}
      cancellationError={cancelSubscription.error?.message ?? null}
      currentTier={getSettingsTier(currentUser.data)}
      onCancelSubscription={() => cancelSubscription.mutate()}
      onManageStore={(type) => {
        window.location.href =
          type === "APPLE"
            ? "https://apps.apple.com/account/subscriptions"
            : "https://play.google.com/store/account/subscriptions";
      }}
      onPeriodChange={setPeriod}
      onPurchase={(tier) => purchase.mutate(tier)}
      planPrices={{
        MAX: products.data?.MAX
          ? getPackagePrice(products.data.MAX, period)
          : null,
        PRO: products.data?.PRO
          ? getPackagePrice(products.data.PRO, period)
          : null,
      }}
      period={period}
      productsLoading={products.isPending}
      purchaseError={
        purchase.error
          ? getPurchaseErrorMessage(purchase.error)
          : (products.error?.message ?? null)
      }
      purchased={purchase.isSuccess}
      purchasingTier={purchase.isPending ? purchase.variables : null}
      subscription={{
        endDate: subscription?.end_date ?? null,
        status: subscription?.status ?? null,
        type: subscription?.type ?? null,
      }}
    />
  );
}
